import { memo, type ReactElement } from 'react';
import type { Message } from '@chatapp-inbox/shared';
import { AttachmentView } from './AttachmentView';
import { KIND_LABEL } from './attachments';
import { formatClock } from './format';
import {
  SENDING_TEXT,
  STILL_SENDING_TEXT,
  UNCONFIRMED_TEXT,
  isStale,
  metaHint,
  retryLabel,
  statusText,
} from './message-status';
import type { LocalSend } from './outbox';
import { messageElementId } from './use-chat-scroll';
import styles from './MessageItem.module.css';

/** Mensagem sem texto (por exemplo, uma mídia que a interface ainda não mostra). */
const EMPTY_TEXT = 'Mensagem sem texto';

type StatusKind = 'pending' | 'sent' | 'delivered' | 'read' | 'error';

export interface MessageItemProps {
  messageKey: string;
  message: Message;
  /** Envio desta aba ainda não confirmado pelo servidor (ou recusado). */
  local: LocalSend | null;
  /** Nome de quem escreve do outro lado (rótulo das mensagens recebidas). */
  contactName: string;
  viewerId: string;
  canWrite: boolean;
  /** "Agora" em ms, para o "Ainda enviando…"; muda só quando alguma mensagem passa dos 30 s. */
  now: number;
  /** Desde quando está "enviando" (ms): só importa enquanto a mensagem está pendente. */
  since: number;
  retrying: boolean;
  retryError: string | null;
  onRetry: (message: Message) => void;
  onRetryLocal: (local: LocalSend) => void;
  onEditLocal: (local: LocalSend) => void;
  onDiscardLocal: (local: LocalSend) => void;
}

const SIZE = { width: 14, height: 14 } as const;

/** Ícone do estado. Decorativo: o texto ao lado diz o mesmo (a cor ou a forma nunca é a única pista). */
function StatusIcon({ kind }: { kind: StatusKind }): ReactElement {
  const common = {
    ...SIZE,
    viewBox: '0 0 16 16',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.6,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
    focusable: false,
    className: styles.icon,
  } as const;
  switch (kind) {
    case 'pending':
      return (
        <svg {...common}>
          <circle cx="8" cy="8" r="6" />
          <path d="M8 4.5V8l2.2 1.4" />
        </svg>
      );
    case 'sent':
      return (
        <svg {...common}>
          <path d="M3.5 8.5l3 3 6-7" />
        </svg>
      );
    case 'delivered':
    case 'read':
      return (
        <svg {...common}>
          <path d="M1.5 8.5l3 3 6-7M7 11.5l1 .01 6-7" />
        </svg>
      );
    case 'error':
      return (
        <svg {...common}>
          <path d="M8 2l6.5 11.5h-13z" />
          <path d="M8 6.5v3M8 11.6v.01" />
        </svg>
      );
  }
}

/**
 * Estado a mostrar numa mensagem de saída: texto, ícone e se é falha. Um envio local tem estados
 * próprios (esperando a vez, sem confirmação, recusado); os demais vêm do servidor.
 */
function describeStatus(
  message: Message,
  local: LocalSend | null,
  since: number,
  now: number,
): { text: string; kind: StatusKind } {
  if (local) {
    if (local.state === 'unconfirmed') return { text: UNCONFIRMED_TEXT, kind: 'error' };
    if (local.state === 'rejected') {
      return { text: local.error ?? 'Não foi possível enviar.', kind: 'error' };
    }
    return {
      text: isStale(since, now) ? STILL_SENDING_TEXT : SENDING_TEXT,
      kind: 'pending',
    };
  }
  const stale = message.status === 'pending' && isStale(since, now);
  return { text: statusText(message, stale), kind: message.status };
}

function MessageItemView({
  messageKey,
  message,
  local,
  contactName,
  viewerId,
  canWrite,
  now,
  since,
  retrying,
  retryError,
  onRetry,
  onRetryLocal,
  onEditLocal,
  onDiscardLocal,
}: MessageItemProps): ReactElement {
  const outgoing = message.direction === 'out';
  // Sussurro: só a equipe vê. Não é "enviado a alguém": sem estado de envio, sem reenvio.
  const whisper = message.internal === true;
  const mine = outgoing && (local !== null || message.authorUserId === viewerId);
  const author = !outgoing ? contactName : mine ? 'Você' : (message.authorName ?? 'Atendente');
  const time = formatClock(message.createdAt);
  const status = outgoing && !whisper ? describeStatus(message, local, since, now) : null;
  const failed = status?.kind === 'error';

  const elementId = messageElementId(messageKey);
  const textId = `${elementId}-text`;
  const attachment = message.attachment;
  const hasText = message.text.trim() !== '';
  // Mensagem só com o arquivo não é "sem texto": o arquivo é o conteúdo.
  const body = hasText ? message.text : attachment ? '' : EMPTY_TEXT;
  const template = message.template;
  const described = [
    template ? `Modelo ${template.name}` : '',
    attachment ? KIND_LABEL[attachment.kind] : '',
    body,
  ]
    .filter(Boolean)
    .join(': ');
  const label = `${whisper ? `Sussurro (só a equipe vê) de ${author}` : author}, ${time}: ${described}${status ? ` — ${status.text}` : ''}${
    failed && message.errorDetail ? ` (${message.errorDetail})` : ''
  }`;

  const serverFailure = failed && local === null;

  return (
    <li
      id={elementId}
      // Recebe o foco por código (depois de reenviar ou descartar) para o foco não cair no <body>.
      tabIndex={-1}
      aria-label={label}
      className={`${styles.item} ${outgoing ? styles.out : styles.in} ${whisper ? styles.whisper : ''}`}
      data-direction={message.direction}
      data-status={status?.kind}
    >
      <div className={`${styles.bubble} ${failed ? styles.failed : ''}`}>
        {whisper && (
          <p className={styles.whisperTag}>
            <span aria-hidden="true">🔒 </span>Sussurro · só a equipe vê
          </p>
        )}
        {outgoing && (!mine || whisper) && (
          <p className={styles.author} dir="auto">
            {author}
          </p>
        )}
        {template && (
          <p className={styles.templateTag} dir="auto">
            Modelo de mensagem: {template.name}
          </p>
        )}
        {attachment && <AttachmentView attachment={attachment} local={local} author={author} />}
        {/* Sempre texto puro: nada de HTML nem links. As quebras de linha ficam (pre-wrap). */}
        {hasText || !attachment ? (
          <p id={textId} className={`${styles.text} ${hasText ? '' : styles.noText}`} dir="auto">
            {body}
          </p>
        ) : (
          // Sem texto, os botões de reenvio ainda precisam de uma descrição ("Foto: contrato.png").
          <span id={textId} className="sr-only">
            {KIND_LABEL[attachment.kind]}
            {attachment.fileName ? `: ${attachment.fileName}` : ''}
          </span>
        )}
        {/* O que o WhatsApp disse ao recusar (código e texto curto): ajuda a achar a causa (modelo, número, conta). */}
        {serverFailure && message.errorDetail && (
          <p className={styles.failureDetail} dir="auto">
            Motivo informado pelo WhatsApp: {message.errorDetail}
          </p>
        )}
        {serverFailure && metaHint(message.errorDetail) && (
          <p className={styles.failureDetail} dir="auto">
            <strong>O que fazer:</strong> {metaHint(message.errorDetail)}
          </p>
        )}
        <p className={styles.meta}>
          <time dateTime={message.createdAt}>{time}</time>
          {status && (
            <span className={`${styles.status} ${failed ? styles.statusFailed : ''}`}>
              <StatusIcon kind={status.kind} />
              <span>{status.text}</span>
            </span>
          )}
        </p>
      </div>

      {serverFailure && canWrite && (
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.action}
            aria-describedby={textId}
            aria-disabled={retrying || undefined}
            onClick={() => {
              if (!retrying) onRetry(message);
            }}
          >
            {retryLabel(message.errorCode)}
          </button>
          {/* A região existe antes do texto, para o leitor de tela anunciar o erro quando chegar. */}
          <span role="alert" className={styles.actionError}>
            {retryError}
          </span>
        </div>
      )}

      {local && (local.state === 'unconfirmed' || local.state === 'rejected') && (
        <div className={styles.actions}>
          {(local.state === 'unconfirmed' || local.retryable) && (
            <button
              type="button"
              className={styles.action}
              aria-describedby={textId}
              onClick={() => onRetryLocal(local)}
            >
              Tentar de novo
            </button>
          )}
          {local.state === 'rejected' && canWrite && (
            <button
              type="button"
              className={styles.action}
              aria-describedby={textId}
              onClick={() => onEditLocal(local)}
            >
              Editar
            </button>
          )}
          <button
            type="button"
            className={styles.action}
            aria-describedby={textId}
            onClick={() => onDiscardLocal(local)}
          >
            Descartar
          </button>
        </div>
      )}
    </li>
  );
}

export const MessageItem = memo(MessageItemView);
